import React, { useEffect, useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import HumanResourcesHeader from 'routes/HumanResourcesModule/HumanResourcesHome/HumanResourcesHeader';
import NotFound from 'components/Errors/NotFound';
import StyContentWrapper from 'routes/HumanResourcesModule/components/ui/StyContentWrapper';
import { useTranslation } from 'react-i18next';
import StyHrPageContainer from 'routes/HumanResourcesModule/components/ui/StyHrPageContainer';
import SynthesisHeaderLoader from 'routes/HumanResourcesModule/PayState/PayStateSynthesis/components/SynthesisHeader/SynthesisHeaderLoader';
import { useGetRetrospective } from '../helpers/hooks/retrospective';
import SynthesisHeader from './components/SynthesisHeader/SynthesisHeader';
import Flex from 'ui/Flex/Flex';
import { SalaryMass } from './SalaryMass/SalaryMass';
import { StyAlert } from 'routes/HumanResourcesModule/components/ui/StyAlert';
import { EnveloppesAmounts } from './Envelopes/EnvelopesAmounts';
import { GVTAndNoriaEffect } from './NoriaGVT/NoriaGVT';

function LoadingPage(): React.ReactElement<[]> {
    const { t } = useTranslation();
    return (
        <StyHrPageContainer className="page-container dx-viewport">
            <HumanResourcesHeader title={t('header.retrospectives')} />
            <StyContentWrapper>
                <Box sx={{ width: '100%' }}>
                    <SynthesisHeaderLoader />
                </Box>
            </StyContentWrapper>
        </StyHrPageContainer>
    );
}
const ComponentsWrapper = (props: { children: React.ReactNode }) => {
    return (
        <Flex direction="column" margin="2em 0" gap="1em">
            {props.children}
        </Flex>
    );
};
const DOC_LINK =
    'https://docs.manty.eu/comment-sont-calcul%C3%A9-les-indicateurs-des-retrospectives?hs_preview=PZwmRLsE-173208527780';

export default function RetrospectiveSynthesis(): React.ReactElement<[]> {
    const { t } = useTranslation();
    const { id = '' } = useParams();
    const [show, setShow] = useState(true);

    const [shouldRedirect, setShouldRedirect] = useState(false);
    const {
        data: RetrospectiveData,
        isLoading: isRetrospectiveLoading,
        isError: isRetrospectiveError,
    } = useGetRetrospective(parseInt(id));
    // if an error occur on the getRetrospective request
    // the user is redirected to the homepage after 5sc.
    useEffect(() => {
        if (!isRetrospectiveLoading && isRetrospectiveError) {
            setTimeout(() => {
                setShouldRedirect(true);
            }, 5000);
        }
    }, [isRetrospectiveError, isRetrospectiveLoading]);

    if (isRetrospectiveLoading) {
        return <LoadingPage />;
    }

    return (
        <StyHrPageContainer
            className="page-container dx-viewport"
            sx={{ display: 'flex', flexDirection: 'column' }}
        >
            <Box sx={{ flex: 0 }}>
                <HumanResourcesHeader title={t('header.retrospectives')} />
            </Box>
            <StyContentWrapper>
                <Box sx={{ width: '100%' }}>
                    {isRetrospectiveLoading ? (
                        <SynthesisHeaderLoader />
                    ) : (
                        <>
                            {isRetrospectiveError ? (
                                <>
                                    {shouldRedirect ? (
                                        <Navigate
                                            to="/human_resources/retrospective_home"
                                            replace
                                        />
                                    ) : (
                                        <NotFound />
                                    )}
                                </>
                            ) : (
                                <>
                                    <SynthesisHeader
                                        retrospective={RetrospectiveData!}
                                    />
                                    <ComponentsWrapper>
                                        {show && (
                                            <StyAlert
                                                severity="info"
                                                onClose={() => setShow(false)}
                                            >
                                                {t(
                                                    'humanResources.retrospective.retrospectiveDescription',
                                                )}
                                                <Link
                                                    href={DOC_LINK}
                                                    target="_blank"
                                                >
                                                    {t(
                                                        'humanResources.retrospective.helpLink',
                                                    )}
                                                </Link>
                                            </StyAlert>
                                        )}

                                        <SalaryMass
                                            retrospective={RetrospectiveData!}
                                        />
                                        <EnveloppesAmounts
                                            retrospective={RetrospectiveData!}
                                        />
                                        <GVTAndNoriaEffect
                                            retrospective={RetrospectiveData!}
                                        />
                                    </ComponentsWrapper>
                                </>
                            )}
                        </>
                    )}
                </Box>
            </StyContentWrapper>
        </StyHrPageContainer>
    );
}
